<template>
    <v-dialog 
      v-model="showModal" 
      :max-width="isMobile ? '95vw' : '1200px'" 
      :fullscreen="isMobile"
      scrollable
      persistent
    >
      <v-card class="editor-card">
        <!-- Header -->
        <v-card-title class="editor-header pa-6">
          <div class="d-flex justify-space-between align-center w-100">
            <div class="d-flex align-center">
              <v-icon size="32" color="white" class="mr-3">mdi-pencil</v-icon>
              <div>
                <h2 class="text-h5 text-white font-weight-bold mb-1">Editor do Changelog</h2>
                <p class="text-body-2 text-white opacity-90 mb-0">Gerencie as versões do sistema</p>
              </div>
            </div>
            <div class="d-flex align-center">
              <v-btn 
                @click="downloadJson"
                icon 
                variant="text" 
                color="white"
                class="mr-2"
                title="Baixar JSON"
              >
                <v-icon>mdi-download</v-icon>
              </v-btn>
              <v-btn 
                icon 
                variant="text" 
                color="white"
                @click="closeEditor"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card-title>
  
        <v-card-text class="editor-content pa-6">
          <!-- Alerta informativo -->
          <v-alert 
            type="info" 
            variant="tonal"
            class="mb-6"
            border="start"
            border-color="info"
          >
            <template v-slot:prepend>
              <v-icon>mdi-information</v-icon>
            </template>
            <div>
              <h4 class="text-h6 mb-2">Como usar o editor:</h4>
              <p class="mb-2">1. Edite as versões conforme necessário</p>
              <p class="mb-2">2. Clique em "Salvar e Baixar" para baixar o arquivo JSON</p>
              <p class="mb-0">3. Substitua o arquivo <strong>public/changelog.json</strong> pelo arquivo baixado</p>
            </div>
          </v-alert>
  
          <!-- Botão Adicionar Nova Versão -->
          <div class="mb-6">
            <v-btn 
              @click="addNewVersion" 
              color="success" 
              size="large"
              prepend-icon="mdi-plus"
              block
            >
              Adicionar Nova Versão
            </v-btn>
          </div>
  
          <!-- Lista de Versões -->
          <div v-if="localChangelog.length > 0">
            <v-expansion-panels 
              v-model="openPanels" 
              multiple
              variant="accordion"
              class="editor-panels"
            >
              <v-expansion-panel
                v-for="(version, versionIndex) in localChangelog"
                :key="versionIndex"
                :value="versionIndex"
                class="version-panel mb-4"
              >
                <v-expansion-panel-title class="version-header">
                  <div class="d-flex justify-space-between align-center w-100">
                    <div class="d-flex align-center">
                      <v-avatar color="primary" size="40" class="mr-4">
                        <span class="text-white font-weight-bold">
                          {{ versionIndex + 1 }}
                        </span>
                      </v-avatar>
                      <div>
                        <h3 class="text-h6 font-weight-bold">
                          Versão {{ version.version }}
                        </h3>
                        <p class="text-body-2 text-grey-darken-1 mb-0">
                          {{ version.date }}
                        </p>
                      </div>
                    </div>
                    <v-btn
                      @click.stop="removeVersion(versionIndex)"
                      icon
                      size="small"
                      color="error"
                      variant="text"
                      class="mr-4"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-title>
  
                <v-expansion-panel-text class="version-content pa-6">
                  <!-- Campos da Versão -->
                  <v-row class="mb-6">
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="version.version"
                        label="Número da Versão"
                        placeholder="Ex: 9.12.014"
                        prepend-icon="mdi-tag"
                        variant="outlined"
                        density="comfortable"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="version.date"
                        label="Data da Versão"
                        placeholder="Ex: 12/06/2025"
                        prepend-icon="mdi-calendar"
                        variant="outlined"
                        density="comfortable"
                      ></v-text-field>
                    </v-col>
                  </v-row>
  
                  <!-- Seções -->
                  <div class="sections-container">
                    <h4 class="text-h6 mb-4 d-flex align-center">
                      <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
                      Seções de Alterações
                    </h4>
  
                    <div 
                      v-for="(items, sectionName, sectionIndex) in version.sections" 
                      :key="sectionIndex"
                      class="section-editor mb-6"
                    >
                      <v-card variant="tonal" :color="getSectionColor(sectionName)" class="pa-4">
                        <!-- Header da Seção -->
                        <div class="d-flex align-center justify-space-between mb-4">
                          <div class="d-flex align-center">
                            <v-icon 
                              :color="getSectionColor(sectionName)" 
                              class="mr-2"
                            >
                              {{ getSectionIcon(sectionName) }}
                            </v-icon>
                            <v-select
                              :model-value="sectionName"
                              @update:model-value="updateSectionName(versionIndex, sectionName, $event)"
                              :items="sectionTypes"
                              label="Tipo de Seção"
                              variant="outlined"
                              density="compact"
                              style="min-width: 200px;"
                            ></v-select>
                          </div>
                          <v-btn
                            @click="removeSection(versionIndex, sectionName)"
                            icon
                            size="small"
                            color="error"
                            variant="text"
                          >
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </div>
  
                        <!-- Items da Seção -->
                        <div class="items-container">
                          <div 
                            v-for="(item, itemIndex) in items" 
                            :key="itemIndex"
                            class="item-editor mb-3"
                          >
                            <div class="d-flex align-center">
                              <v-textarea
                                v-model="version.sections[sectionName][itemIndex]"
                                :label="`Item ${itemIndex + 1}`"
                                variant="outlined"
                                density="compact"
                                rows="2"
                                auto-grow
                                class="flex-grow-1 mr-2"
                              ></v-textarea>
                              <v-btn
                                @click="removeItem(versionIndex, sectionName, itemIndex)"
                                icon
                                size="small"
                                color="error"
                                variant="text"
                              >
                                <v-icon>mdi-close</v-icon>
                              </v-btn>
                            </div>
                          </div>
  
                          <!-- Botão Adicionar Item -->
                          <v-btn
                            @click="addItem(versionIndex, sectionName)"
                            color="primary"
                            variant="outlined"
                            prepend-icon="mdi-plus"
                            size="small"
                            class="mt-2"
                          >
                            Adicionar Item
                          </v-btn>
                        </div>
                      </v-card>
                    </div>
  
                    <!-- Botão Adicionar Seção -->
                    <v-btn
                      @click="addSection(versionIndex)"
                      color="info"
                      variant="outlined"
                      prepend-icon="mdi-plus"
                      block
                    >
                      Adicionar Nova Seção
                    </v-btn>
                  </div>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
  
          <!-- Empty State -->
          <div v-else class="empty-state text-center py-12">
            <v-icon size="120" color="grey-lighten-2">mdi-file-document-plus</v-icon>
            <h3 class="text-h5 mt-6 mb-3">Nenhuma versão criada</h3>
            <p class="text-body-1 text-grey mb-6">
              Clique no botão acima para adicionar sua primeira versão.
            </p>
          </div>
        </v-card-text>
  
        <!-- Footer Actions -->
        <v-divider></v-divider>
        <v-card-actions class="pa-6">
          <v-btn 
            @click="resetChanges"
            color="warning"
            variant="outlined"
            prepend-icon="mdi-restore"
          >
            Resetar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn 
            @click="closeEditor"
            color="grey"
            variant="outlined"
            class="mr-3"
          >
            Cancelar
          </v-btn>
          <v-btn 
            @click="saveChanges"
            color="success"
            variant="flat"
            prepend-icon="mdi-content-save"
            size="large"
            :loading="saving"
          >
            Salvar e Baixar
          </v-btn>
        </v-card-actions>
      </v-card>
  
      <!-- Dialog de Confirmação -->
      <v-dialog v-model="showConfirmDialog" max-width="400">
        <v-card>
          <v-card-title class="text-h6">Confirmar Ação</v-card-title>
          <v-card-text>{{ confirmMessage }}</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn @click="showConfirmDialog = false" color="grey">Cancelar</v-btn>
            <v-btn @click="confirmAction" color="error">Confirmar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-dialog>
  </template>
  
  <script>
  export default {
    name: 'ChangelogEditor',
    props: {
      showEditorModal: Boolean,
    },
    data() {
      return {
        showModal: this.showEditorModal,
        localChangelog: [],
        originalChangelog: [],
        openPanels: [0],
        saving: false,
        showConfirmDialog: false,
        confirmMessage: '',
        confirmCallback: null,
        sectionTypes: [
          'Adicionado',
          'Melhorado', 
          'Corrigido',
          'Removido',
          'Alterado',
          'Atualizado',
          'Segurança'
        ]
      };
    },
    computed: {
      isMobile() {
        return this.$vuetify.display.mobile;
      }
    },
    watch: {
      showEditorModal: {
        handler(newValue) {
          this.showModal = newValue;
          if (newValue) {
            this.loadChangelog();
          }
        },
        immediate: true,
      },
    },
    methods: {
      async loadChangelog() {
        try {
          const response = await fetch('./public/changelog.json');
          if (response.ok) {
            const data = await response.json();
            this.originalChangelog = JSON.parse(JSON.stringify(data.versions || []));
            this.localChangelog = JSON.parse(JSON.stringify(data.versions || []));
          } else {
            this.localChangelog = [];
            this.originalChangelog = [];
          }
          this.openPanels = this.localChangelog.length > 0 ? [0] : [];
        } catch (error) {
          console.error('Erro ao carregar changelog:', error);
          this.localChangelog = [];
          this.originalChangelog = [];
        }
      },
  
      addNewVersion() {
        const newVersion = {
          version: '',
          date: new Date().toLocaleDateString('pt-BR'),
          sections: {
            'Adicionado': ['']
          }
        };
        this.localChangelog.unshift(newVersion);
        this.openPanels = [0];
      },
  
      addSection(versionIndex) {
        const availableSections = this.sectionTypes.filter(
          type => !this.localChangelog[versionIndex].sections[type]
        );
        
        if (availableSections.length > 0) {
          const sectionName = availableSections[0];
          // Vue 3 - não precisa do $set, apenas atribua diretamente
          this.localChangelog[versionIndex].sections[sectionName] = [''];
        }
      },
  
      addItem(versionIndex, sectionName) {
        this.localChangelog[versionIndex].sections[sectionName].push('');
      },
  
      removeVersion(versionIndex) {
        this.confirmMessage = `Tem certeza que deseja remover a versão ${this.localChangelog[versionIndex].version}?`;
        this.confirmCallback = () => {
          this.localChangelog.splice(versionIndex, 1);
          this.showConfirmDialog = false;
        };
        this.showConfirmDialog = true;
      },
  
      removeSection(versionIndex, sectionName) {
        this.confirmMessage = `Tem certeza que deseja remover a seção "${sectionName}"?`;
        this.confirmCallback = () => {
          // Vue 3 - use delete operator
          delete this.localChangelog[versionIndex].sections[sectionName];
          this.showConfirmDialog = false;
        };
        this.showConfirmDialog = true;
      },
  
      removeItem(versionIndex, sectionName, itemIndex) {
        this.localChangelog[versionIndex].sections[sectionName].splice(itemIndex, 1);
        
        // Remove seção se não tiver mais itens
        if (this.localChangelog[versionIndex].sections[sectionName].length === 0) {
          delete this.localChangelog[versionIndex].sections[sectionName];
        }
      },
  
      updateSectionName(versionIndex, oldName, newName) {
        if (oldName !== newName && !this.localChangelog[versionIndex].sections[newName]) {
          const items = this.localChangelog[versionIndex].sections[oldName];
          // Vue 3 - atribua diretamente e delete o antigo
          this.localChangelog[versionIndex].sections[newName] = items;
          delete this.localChangelog[versionIndex].sections[oldName];
        }
      },
  
      async saveChanges() {
        this.saving = true;
        
        try {
          // Validar dados
          const validChangelog = this.localChangelog.filter(version => 
            version.version && version.date && Object.keys(version.sections).length > 0
          );
  
          // Limpar itens vazios
          validChangelog.forEach(version => {
            Object.keys(version.sections).forEach(sectionName => {
              version.sections[sectionName] = version.sections[sectionName].filter(item => item.trim());
              if (version.sections[sectionName].length === 0) {
                delete version.sections[sectionName];
              }
            });
          });
  
          const jsonData = {
            versions: validChangelog
          };
  
          // Simular salvamento
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Criar e baixar arquivo
          this.downloadJsonFile(jsonData);
          
          this.$emit('changelog-updated', validChangelog);
          this.closeEditor();
          
        } catch (error) {
          console.error('Erro ao salvar:', error);
          alert('Erro ao salvar as alterações!');
        } finally {
          this.saving = false;
        }
      },
  
      downloadJson() {
        const jsonData = {
          versions: this.localChangelog
        };
        this.downloadJsonFile(jsonData);
      },
  
      downloadJsonFile(data) {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'changelog.json';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
  
      resetChanges() {
        this.confirmMessage = 'Tem certeza que deseja resetar todas as alterações?';
        this.confirmCallback = () => {
          this.localChangelog = JSON.parse(JSON.stringify(this.originalChangelog));
          this.showConfirmDialog = false;
        };
        this.showConfirmDialog = true;
      },
  
      confirmAction() {
        if (this.confirmCallback) {
          this.confirmCallback();
        }
      },
  
      closeEditor() {
        this.$emit("update:showEditorModal", false);
      },
  
      getSectionColor(sectionName) {
        const colors = {
          'Adicionado': 'success',
          'Melhorado': 'info',
          'Corrigido': 'warning',
          'Removido': 'error',
          'Alterado': 'purple',
          'Atualizado': 'teal',
          'Segurança': 'red-darken-2'
        };
        return colors[sectionName] || 'primary';
      },
  
      getSectionIcon(sectionName) {
        const icons = {
          'Adicionado': 'mdi-plus-circle',
          'Melhorado': 'mdi-arrow-up-circle',
          'Corrigido': 'mdi-wrench',
          'Removido': 'mdi-minus-circle',
          'Alterado': 'mdi-pencil-circle',
          'Atualizado': 'mdi-update',
          'Segurança': 'mdi-shield-check'
        };
        return icons[sectionName] || 'mdi-information';
      },
    },
  };
  </script>
  
  <style scoped>
  .editor-card {
    border-radius: 16px !important;
    overflow: hidden;
  }
  
  .editor-header {
    background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
  }
  
  .editor-content {
    max-height: 70vh;
    overflow-y: auto;
  }
  
  .editor-panels {
    gap: 16px;
  }
  
  .version-panel {
    border-radius: 12px !important;
    border: 1px solid rgba(0, 0, 0, 0.08);
  }
  
  .version-header {
    padding: 20px 24px !important;
    background: rgba(0, 0, 0, 0.02);
  }
  
  .version-content {
    background: #fafafa;
  }
  
  .section-editor {
    border-radius: 12px !important;
  }
  
  .item-editor {
    background: rgba(255, 255, 255, 0.7);
    border-radius: 8px;
    padding: 12px;
  }
  
  .empty-state {
    color: #666;
  }
  
  /* Scrollbar personalizada */
  .editor-content::-webkit-scrollbar {
    width: 8px;
  }
  
  .editor-content::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  .editor-content::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  .editor-content::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
  
  @media (max-width: 600px) {
    .editor-content {
      max-height: calc(100vh - 200px);
      padding: 16px !important;
    }
    
    .version-header {
      padding: 16px !important;
    }
  }
  </style>